import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Settings, Bell, FileText, Wrench, AlertCircle } from 'lucide-react';
import onboardingService from '../../../services/onboardingService';
import { useAuth } from '../../../contexts/AuthContext';

const DEFAULT_CONFIGURATION = {
  reminderSettings: {
    expirationWarningDays: [30, 15, 7, 1],
    emailNotifications: true,
    smsNotifications: false,
    customWarningDays: ''
  },
  maintenanceSettings: {
    defaultIntervalMiles: 10000,
    defaultIntervalDays: 90,
    reminderDaysBefore: 7,
    trackMaintenanceByMiles: true,
    trackMaintenanceByDays: true
  },
  documentSettings: {
    requireDocumentApproval: false,
    allowDriverUploads: true,
    autoArchiveExpired: true,
    reminderFrequency: 'weekly'
  },
  complianceSettings: {
    enableAutomaticReports: true,
    reportFrequency: 'monthly',
    includePendingItems: true,
    notifyOnViolations: true
  }
};

const stableStringify = (value) => {
  if (value === undefined) {
    return 'null';
  }

  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }

  return JSON.stringify(value);
};

const deepEqual = (a, b) => stableStringify(a) === stableStringify(b);

const stripConfigurationMeta = (raw = {}) => {
  const {
    isPartial,
    lastSaved,
    readyForSubmission,
    createdAt,
    updatedAt,
    metadata,
    lastSavedAt,
    ...rest
  } = raw;
  return rest;
};

const normalizeConfiguration = (raw = {}) => {
  const sanitized = stripConfigurationMeta(raw);
  return {
    reminderSettings: {
      ...DEFAULT_CONFIGURATION.reminderSettings,
      ...(sanitized.reminderSettings || {})
    },
    maintenanceSettings: {
      ...DEFAULT_CONFIGURATION.maintenanceSettings,
      ...(sanitized.maintenanceSettings || {})
    },
    documentSettings: {
      ...DEFAULT_CONFIGURATION.documentSettings,
      ...(sanitized.documentSettings || {})
    },
    complianceSettings: {
      ...DEFAULT_CONFIGURATION.complianceSettings,
      ...(sanitized.complianceSettings || {})
    }
  };
};

const InitialConfigStep = ({ data, updateData, registerNextHandler }) => {
  const { currentUser } = useAuth();
  const normalizedConfigFromProps = useMemo(
    () => normalizeConfiguration(data?.configuration || {}),
    [data?.configuration]
  );

  const [formData, setFormData] = useState(normalizedConfigFromProps);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const lastSyncedConfigRef = useRef(null);

  useEffect(() => {
    setFormData(prev => (deepEqual(prev, normalizedConfigFromProps) ? prev : normalizedConfigFromProps));
  }, [normalizedConfigFromProps]);

  useEffect(() => {
    const serialized = JSON.stringify(formData);
    if (serialized === lastSyncedConfigRef.current) {
      return;
    }

    lastSyncedConfigRef.current = serialized;
    const clonedData = JSON.parse(serialized);
    updateData('configuration', clonedData, { skipRemote: true });
  }, [formData, updateData]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleCheckboxChange = (section, field) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: !prev[section][field]
      }
    }));
  };

  const handleArrayChange = (section, field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) => (i === index ? Number(value) || 0 : item))
      }
    }));
  };

  const addCustomWarningDay = () => {
    const customDay = Number(formData.reminderSettings.customWarningDays);
    if (Number.isInteger(customDay) && customDay > 0 && customDay <= 365) {
      if (!formData.reminderSettings.expirationWarningDays.includes(customDay)) {
        setFormData(prev => ({
          ...prev,
          reminderSettings: {
            ...prev.reminderSettings,
            expirationWarningDays: [...prev.reminderSettings.expirationWarningDays, customDay].sort((a, b) => b - a),
            customWarningDays: ''
          }
        }));
      }
    }
  };

  const removeWarningDay = (dayToRemove) => {
    setFormData(prev => ({
      ...prev,
      reminderSettings: {
        ...prev.reminderSettings,
        expirationWarningDays: prev.reminderSettings.expirationWarningDays.filter(day => day !== dayToRemove)
      }
    }));
  };

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (formData.maintenanceSettings.defaultIntervalMiles < 1000) {
      newErrors.maintenanceInterval = 'Maintenance interval must be at least 1,000 miles';
    }

    if (formData.maintenanceSettings.defaultIntervalDays < 30) {
      newErrors.maintenanceDays = 'Maintenance interval must be at least 30 days';
    }

    if (formData.reminderSettings.expirationWarningDays.length === 0) {
      newErrors.warningDays = 'At least one warning day is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  useEffect(() => {
    if (!registerNextHandler) {
      return undefined;
    }

    const unregister = registerNextHandler(async () => {
      const isValid = validateForm();
      if (!isValid) {
        console.warn('⚠️ CONFIG-STEP: Validation failed, blocking Next progression');
        return false;
      }

      if (!currentUser?.uid) {
        setErrors(prev => ({
          ...prev,
          submit: 'You must be signed in to continue the onboarding process.'
        }));
        return false;
      }

      try {
        setIsSaving(true);
        setErrors(prev => ({ ...prev, submit: undefined }));

        const payload = JSON.parse(JSON.stringify(formData));

        console.log('📡 CONFIG-STEP: Saving configuration data via API before advancing', {
          userId: currentUser.uid,
          payload
        });

        await onboardingService.saveConfigurationDataViaApi(currentUser.uid, payload, false);
        lastSyncedConfigRef.current = JSON.stringify(payload);
        console.log('✅ CONFIG-STEP: Configuration data saved successfully via API');
        return true;
      } catch (error) {
        console.error('❌ CONFIG-STEP: Failed to save configuration data', error);
        setErrors(prev => ({
          ...prev,
          submit: error?.response?.data?.error || error.message || 'Failed to save configuration data. Please try again.'
        }));
        return false;
      } finally {
        setIsSaving(false);
      }
    });

    return () => unregister?.();
  }, [registerNextHandler, validateForm, formData, updateData, currentUser?.uid]);

  return (
    <form onSubmit={(event) => event.preventDefault()} className="space-y-8">
      {errors.submit && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{errors.submit}</p>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Bell className="h-5 w-5 mr-2 text-blue-600" />
          Notification Settings
        </h3>

        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Send expiration warnings (days before expiration)
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.reminderSettings.expirationWarningDays.map(day => (
                <span
                  key={day}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                >
                  {day} day{day !== 1 ? 's' : ''}
                  <button
                    type="button"
                    onClick={() => removeWarningDay(day)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={formData.reminderSettings.customWarningDays}
                onChange={(event) => handleInputChange('reminderSettings', 'customWarningDays', event.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Add custom warning days"
              />
              <button
                type="button"
                onClick={addCustomWarningDay}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Add
              </button>
            </div>
            {errors.warningDays && (
              <p className="mt-1 text-sm text-red-600">{errors.warningDays}</p>
            )}
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.reminderSettings.emailNotifications}
                onChange={() => handleCheckboxChange('reminderSettings', 'emailNotifications')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Email notifications</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.reminderSettings.smsNotifications}
                onChange={() => handleCheckboxChange('reminderSettings', 'smsNotifications')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">SMS notifications</span>
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Wrench className="h-5 w-5 mr-2 text-blue-600" />
          Maintenance Settings
        </h3>

        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default maintenance interval (miles)
              </label>
              <input
                type="number"
                min="1000"
                max="50000"
                value={formData.maintenanceSettings.defaultIntervalMiles}
                onChange={(event) => handleInputChange('maintenanceSettings', 'defaultIntervalMiles', Number(event.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.maintenanceInterval && (
                <p className="mt-1 text-sm text-red-600">{errors.maintenanceInterval}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default maintenance interval (days)
              </label>
              <input
                type="number"
                min="30"
                max="365"
                value={formData.maintenanceSettings.defaultIntervalDays}
                onChange={(event) => handleInputChange('maintenanceSettings', 'defaultIntervalDays', Number(event.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {errors.maintenanceDays && (
                <p className="mt-1 text-sm text-red-600">{errors.maintenanceDays}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Send maintenance reminders (days before due)
            </label>
            <input
              type="number"
              min="1"
              max="30"
              value={formData.maintenanceSettings.reminderDaysBefore}
              onChange={(event) => handleInputChange('maintenanceSettings', 'reminderDaysBefore', Number(event.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.maintenanceSettings.trackMaintenanceByMiles}
                onChange={() => handleCheckboxChange('maintenanceSettings', 'trackMaintenanceByMiles')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Track maintenance by mileage</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.maintenanceSettings.trackMaintenanceByDays}
                onChange={() => handleCheckboxChange('maintenanceSettings', 'trackMaintenanceByDays')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Track maintenance by time intervals</span>
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <FileText className="h-5 w-5 mr-2 text-blue-600" />
          Document Management
        </h3>

        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.documentSettings.requireDocumentApproval}
                onChange={() => handleCheckboxChange('documentSettings', 'requireDocumentApproval')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Require approval for new documents</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.documentSettings.allowDriverUploads}
                onChange={() => handleCheckboxChange('documentSettings', 'allowDriverUploads')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Allow drivers to upload documents</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.documentSettings.autoArchiveExpired}
                onChange={() => handleCheckboxChange('documentSettings', 'autoArchiveExpired')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Automatically archive expired documents</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document reminder frequency
            </label>
            <select
              value={formData.documentSettings.reminderFrequency}
              onChange={(event) => handleInputChange('documentSettings', 'reminderFrequency', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center">
          <Settings className="h-5 w-5 mr-2 text-blue-600" />
          Compliance & Reporting
        </h3>

        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.complianceSettings.enableAutomaticReports}
                onChange={() => handleCheckboxChange('complianceSettings', 'enableAutomaticReports')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Generate automatic compliance reports</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.complianceSettings.includePendingItems}
                onChange={() => handleCheckboxChange('complianceSettings', 'includePendingItems')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Include pending items in reports</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.complianceSettings.notifyOnViolations}
                onChange={() => handleCheckboxChange('complianceSettings', 'notifyOnViolations')}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Notify immediately on compliance violations</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report generation frequency
            </label>
            <select
              value={formData.complianceSettings.reportFrequency}
              onChange={(event) => handleInputChange('complianceSettings', 'reportFrequency', event.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Configuration Note
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                These settings can be modified later in the system settings. 
                Use the Next button to continue once you are comfortable with the defaults.
              </p>
            </div>
          </div>
        </div>
      </div>

      {isSaving && (
        <div className="flex items-center pt-2 text-sm text-blue-600">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
          Saving configuration…
        </div>
      )}
    </form>
  );
};

export default InitialConfigStep;
